import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "../../Components/PageLayout";
import { DataTable } from "../../Components/ui/table";
import { DataTableColumn } from "../../Components/ui/table/types";
import { FiltersWrapper } from "../../Components/ui/table/filters";
import { InputFilter } from "../../Components/ui/table/filters/input";
import { SelectFilter } from "../../Components/ui/table/filters/select/select-filter";
import { CursorPagination } from "../../Components/ui/table/pagination";
import { usePaginatedQuery } from "../../Hooks/use-paginated-query";

type InvoiceInstance = {
  id: number;
  maintenance_report?: {
    id?: number;
    order_number?: string | null;
  } | null;
  invoice_number: string;
  supplier?: {
    id?: number;
    name?: string | null;
  } | null;
  invoiced_at: string;
  purchase_order: string;
  created_by?: {
    id?: number;
    username?: string | null;
    full_name?: string | null;
  } | null;
  created_at: string;
  currency: string;
};

const columns: DataTableColumn<InvoiceInstance>[] = [
  {
    header: "Invoice #",
    labelKey: "invoiceNumber",
    field: "invoice_number",
    sortable: true,
  },
  {
    header: "PO",
    labelKey: "purchaseOrder",
    field: "purchase_order",
    sortable: true,
  },
  {
    header: "Supplier",
    labelKey: "supplier",
    accessor: (row) => row.supplier?.name ?? "-",
  },
  {
    header: "Maintenance Report",
    labelKey: "maintenanceOrder",
    accessor: (row) => row.maintenance_report?.order_number ?? "-",
  },
  {
    header: "Invoice Date",
    labelKey: "invoiceDate",
    field: "invoiced_at",
    sortable: true,
  },
  {
    header: "Currency",
    labelKey: "currency",
    field: "currency",
    sortable: true,
  },
  {
    header: "Created By",
    labelKey: "createdBy",
    accessor: (row) => row.created_by?.full_name || row.created_by?.username || "-",
  },
];

const initialUrl = "/safety/invoices/";

const InvoicesRoute = () => {
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState("");
  const [purchaseOrderFilter, setPurchaseOrderFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [orderNumberFilter, setOrderNumberFilter] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("");
  const [createdByFilter, setCreatedByFilter] = useState("");

  const navigate = useNavigate();

  const params = useMemo(
    () => ({
      ...(invoiceNumberFilter.trim() ? { invoice_number: invoiceNumberFilter.trim() } : {}),
      ...(purchaseOrderFilter.trim() ? { purchase_order: purchaseOrderFilter.trim() } : {}),
      ...(supplierFilter.trim() ? { supplier_name: supplierFilter.trim() } : {}),
      ...(orderNumberFilter.trim() ? { order_number: orderNumberFilter.trim() } : {}),
      ...(currencyFilter.trim() ? { currency: currencyFilter.trim().toUpperCase() } : {}),
      ...(createdByFilter.trim() ? { created_by: createdByFilter.trim() } : {}),
    }),
    [invoiceNumberFilter, purchaseOrderFilter, supplierFilter, orderNumberFilter, currencyFilter, createdByFilter],
  );

  const results = usePaginatedQuery<InvoiceInstance>({
    initialUrl,
    params,
  });

  return (
    <PageLayout title="Invoices" header="invoices">
      <FiltersWrapper>
        <InputFilter
          value={invoiceNumberFilter}
          onChange={setInvoiceNumberFilter}
          placeholder="invoiceNumber"
        />
        <InputFilter
          value={purchaseOrderFilter}
          onChange={setPurchaseOrderFilter}
          placeholder="purchaseOrder"
        />
        <InputFilter
          value={supplierFilter}
          onChange={setSupplierFilter}
          placeholder="supplier"
        />
        <InputFilter
          value={orderNumberFilter}
          onChange={setOrderNumberFilter}
          placeholder="maintenanceOrder"
        />
        <InputFilter
          value={createdByFilter}
          onChange={setCreatedByFilter}
          placeholder="createdBy"
        />
        <SelectFilter
          value={currencyFilter}
          onChange={setCurrencyFilter}
          options={[
            { optionLabel: "USD", optionValue: "USD" },
            { optionLabel: "MXN", optionValue: "MXN" },
          ]}
        />
      </FiltersWrapper>

      <CursorPagination
        hasNext={results.hasNext}
        hasPrevious={results.hasPrevious}
        currentPage={results.currentPage}
        fetchNextPage={results.fetchNextPage}
        fetchPreviousPage={results.fetchPreviousPage}
      />

      <DataTable
        columns={columns}
        data={results.results}
        getRowKey={(row) => row.id}
        isLoading={results.isLoading}
        onRowClick={(row) => navigate(`/maintenance-reports/${row.maintenance_report?.order_number}`)}
      />
    </PageLayout>
  );
};

export default InvoicesRoute;